import { useAudio } from '@openeducation/oe-rn-audio-video';
import { Translator } from '@openeducation/oe-rn-shared-types';
import { styled } from '@openeducation/oe-rn-themes';
import { Asset } from 'expo-asset';
import React from 'react';
import { View } from 'react-native';

import { Icon, OEText, OETouchableOpacity, Popup } from '../../atoms';
import { flex } from '../../style-utils';

export interface TranslationPopupProps {
  isVisible: boolean;
  onClose: () => void;
  isLoading: boolean;
  data?: Translator;
  text: string;
  children?: React.ReactNode;
}

export function TranslationPopup({
  isVisible,
  text,
  data,
  isLoading,
  onClose,
  children,
}: TranslationPopupProps) {
  const audioUrl = data?.audioSources[0]?.src;

  const phoneticSound = useAudio({
    audioUrl: Asset.fromURI(audioUrl ?? '')?.uri,
  });

  const handleAudioPress = async () => {
    await phoneticSound.setSoundPosition(0);
    await phoneticSound.playSound();
  };

  const content = () => {
    if (isLoading) {
      return <Text>Loading...</Text>;
    }

    if (data) {
      return (
        <>
          <Text>
            {text} - {data.translation}
          </Text>
          <View style={[flex('flex-row', 'align-items-center')]}>
            <PhoneticText>{data.phoneticSpelling}</PhoneticText>
            {audioUrl && (
              <AudioButton onPress={handleAudioPress}>
                <AudioIcon name="volume-up" />
              </AudioButton>
            )}
          </View>
        </>
      );
    }

    return null;
  };

  return (
    <Popup
      popupPosition="auto"
      isVisible={isVisible}
      popupContent={
        <Container>
          <CloseButton onPress={onClose}>
            <CloseIcon name="times" />
          </CloseButton>
          {content()}
        </Container>
      }
    >
      {children}
    </Popup>
  );
}

const Container = styled.View`
  position: relative;
  padding: 24px 32px;
`;

const CloseButton = styled(OETouchableOpacity)`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const CloseIcon = styled(Icon).attrs({
  size: 16,
})`
  color: ${({ theme }) => theme.color.dark1};
`;

const Text = styled(OEText).attrs({
  numberOfLines: 1,
})`
  color: ${({ theme }) => theme.color.dark1};
  font-size: 14px;
`;

const PhoneticText = styled(Text)`
  color: ${({ theme }) => theme.color.primary};
`;

const AudioButton = styled(OETouchableOpacity)`
  margin-left: 8px;
`;

const AudioIcon = styled(Icon).attrs({
  size: 16,
})`
  color: ${({ theme }) => theme.color.primary};
`;
